// @givmo/board — the officer's reads of the annual conflict-of-interest disclosures, each dark until
// the backend serves it (CBG-YR2-WEB1; the calendar's shape, `calendar/calendar-queries.ts`).
//
// ══ A ROUTE MISS IS "NOT SERVED", NEVER AN ERROR ════════════════════════════════════════════════
//
// While `BOARD_COI_CYCLE_ENABLED` is off every cycle route answers the absent-route 404, and the list
// read here RESOLVES that to `null` — a settled answer meaning "not served", so the Conflicts of
// interest page renders exactly as it did before the year existed, and nothing retries. Any other
// failure stays an error. There is no web flag: the backend's answer is the gate.
//
// One key family, `boardCoiKeys`, so one act refreshes every read of its organization's year — and the
// register, whose annual answers a submission or a correction changes.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BoardCoiCycleOut,
  BoardCoiCycleSummaryOut,
  BoardCoiPersonQuestionnaireOut,
  BoardCoiQuestionnaireOut,
  BoardCoiResponseChangesOut,
  BoardCoiResponseOut,
} from '@givmo/api-client';
import { useBoardOperations } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';
import {
  decodeChanges,
  decodeCycle,
  decodePersonQuestionnaire,
  decodeQuestionnaire,
  decodeResponse,
  extractCycles,
  extractQuestionnaires,
} from './coi-decode';

/** The year's query keys, rooted per organization so one act can refresh them all. */
export function boardCoiKeys(orgRef: string | null) {
  const root = ['board-coi', orgRef ?? ''] as const;
  return {
    root,
    cycles: [...root, 'cycles'] as const,
    cycle: (cycleId: string) => [...root, 'cycle', cycleId] as const,
    questionnaires: [...root, 'questionnaires'] as const,
    questionnaire: (templateId: string) => [...root, 'questionnaire', templateId] as const,
    response: (cycleId: string, recordId: string) =>
      [...root, 'response', cycleId, recordId] as const,
    changes: (cycleId: string, recordId: string) =>
      [...root, 'changes', cycleId, recordId] as const,
    person: (cycleId: string, seatId: string) => [...root, 'person', cycleId, seatId] as const,
  };
}

/** Never retry the route miss: it is an answer, not a blip. */
function retryUnlessNotServed(failureCount: number, error: unknown): boolean {
  return !isNotServed(error) && failureCount < 1;
}

/**
 * Every year of this organization, newest first: `null` while the year is not served here (the
 * module's route miss), the list once it is.
 */
export function useCoiCycles(orgRef: string | null, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCoiKeys(orgRef).cycles,
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiCycleSummaryOut[] | null> => {
      try {
        const { data } = await operations.officerListBoardCoiCycles({
          path: { org_ref: orgRef ?? '' },
          throwOnError: true,
        });
        return extractCycles(data);
      } catch (error) {
        if (isNotServed(error)) return null;
        throw error;
      }
    },
  });
}

/** One year's dashboard. */
export function useCoiCycle(orgRef: string | null, cycleId: string | null) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCoiKeys(orgRef).cycle(cycleId ?? ''),
    enabled: orgRef !== null && cycleId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiCycleOut | null> => {
      const { data } = await operations.officerGetBoardCoiCycle({
        path: { org_ref: orgRef ?? '', cycle_id: cycleId ?? '' },
        throwOnError: true,
      });
      return decodeCycle(data);
    },
  });
}

/** Givmo's starting questionnaire offered to this organization, with its questions (the newest). */
export function useCoiStartingQuestionnaire(orgRef: string | null, enabled: boolean) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCoiKeys(orgRef).questionnaires,
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiQuestionnaireOut | null> => {
      const { data: list } = await operations.officerListBoardCoiQuestionnaires({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      const first = extractQuestionnaires(list)[0];
      if (first === undefined) return null;
      const { data } = await operations.officerGetBoardCoiQuestionnaire({
        path: { org_ref: orgRef ?? '', template_id: first.template_id },
        throwOnError: true,
      });
      return decodeQuestionnaire(data);
    },
  });
}

/**
 * One questionnaire version's words, for the questions a year asked (the redline's short titles): the
 * template a year was opened with, read by its id.
 */
export function useCoiTemplate(orgRef: string | null, templateId: string | null) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCoiKeys(orgRef).questionnaire(templateId ?? ''),
    enabled: orgRef !== null && templateId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiQuestionnaireOut | null> => {
      const { data } = await operations.officerGetBoardCoiQuestionnaire({
        path: { org_ref: orgRef ?? '', template_id: templateId ?? '' },
        throwOnError: true,
      });
      return decodeQuestionnaire(data);
    },
  });
}

/** One response of a year, as recorded (the first, or a correction). */
export function useCoiResponse(orgRef: string | null, cycleId: string, recordId: string | null) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCoiKeys(orgRef).response(cycleId, recordId ?? ''),
    enabled: orgRef !== null && recordId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiResponseOut | null> => {
      const { data } = await operations.officerGetBoardCoiResponse({
        path: { org_ref: orgRef ?? '', cycle_id: cycleId, record_id: recordId ?? '' },
        throwOnError: true,
      });
      return decodeResponse(data);
    },
  });
}

/** One response against the one it replaced. */
export function useCoiChanges(
  orgRef: string | null,
  cycleId: string,
  recordId: string | null,
  enabled: boolean,
) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCoiKeys(orgRef).changes(cycleId, recordId ?? ''),
    enabled: enabled && orgRef !== null && recordId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiResponseChangesOut | null> => {
      const { data } = await operations.officerGetBoardCoiResponseChanges({
        path: { org_ref: orgRef ?? '', cycle_id: cycleId, record_id: recordId ?? '' },
        throwOnError: true,
      });
      return decodeChanges(data);
    },
  });
}

/** One person's questionnaire for the year, for the officer's paper road (CBG-YR2-BE5). */
export function useCoiPersonQuestionnaire(
  orgRef: string | null,
  cycleId: string,
  seatId: string | null,
) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCoiKeys(orgRef).person(cycleId, seatId ?? ''),
    enabled: orgRef !== null && seatId !== null,
    retry: retryUnlessNotServed,
    // A form opened again starts from what the server says now: last year's keys, this year's
    // questions and whether a first answer is still taken.
    staleTime: 0,
    queryFn: async (): Promise<BoardCoiPersonQuestionnaireOut | null> => {
      const { data } = await operations.officerGetBoardCoiPersonQuestionnaire({
        path: { org_ref: orgRef ?? '', cycle_id: cycleId },
        query: { board_member_id: seatId ?? '' },
        throwOnError: true,
      });
      return decodePersonQuestionnaire(data);
    },
  });
}

/** Refresh every read of the year, and the register, after an act. */
export function useRefreshCoi(orgRef: string | null) {
  const queryClient = useQueryClient();
  const operations = useBoardOperations();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: boardCoiKeys(orgRef).root }),
      // The register's annual answers move with a submission or a correction. The generated key
      // without a `query` member is the family prefix, so every filtered register read refreshes.
      queryClient.invalidateQueries({
        queryKey: operations.officerListBoardConflictRegisterQueryKey({
          path: { org_ref: orgRef ?? '' },
        }),
      }),
    ]);
  };
}

/**
 * The answers in force for a person's year: their first response, followed through its corrections
 * to the latest (each correction is a record of its own; the dashboard names the first).
 */
export async function headResponse(
  operations: ReturnType<typeof useBoardOperations>,
  orgRef: string,
  cycleId: string,
  recordId: string,
): Promise<BoardCoiResponseOut | null> {
  let current: BoardCoiResponseOut | null = null;
  let next: string | null = recordId;
  const seen = new Set<string>();
  while (next !== null && !seen.has(next) && seen.size < 20) {
    seen.add(next);
    const { data } = await operations.officerGetBoardCoiResponse({
      path: { org_ref: orgRef, cycle_id: cycleId, record_id: next },
      throwOnError: true,
    });
    const read = decodeResponse(data);
    if (read === null) break;
    current = read;
    next = read.corrected_by_record_id ?? null;
  }
  return current;
}

/** The head of a person's answers for the year (see `headResponse`). */
export function useCoiHeadResponse(
  orgRef: string | null,
  cycleId: string,
  recordId: string | null,
) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: [...boardCoiKeys(orgRef).response(cycleId, recordId ?? ''), 'head'] as const,
    enabled: orgRef !== null && recordId !== null,
    retry: retryUnlessNotServed,
    queryFn: () => headResponse(operations, orgRef ?? '', cycleId, recordId ?? ''),
  });
}
